import React, {
  FormEventHandler,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Button } from '../../components/Button';
import { TextInput } from '../../components/TextInput';
import { useRequest } from '../../hooks/useRequest';
import { BuildingContext } from '../../contexts/BuildingContext';
import { EventTracker, getUserParams, handleRequestError } from '../../utils';
import { Clickable } from '../../components/Clickable';
import { HttpMethod, Request, EmailSettings, IUser } from '@poormanvr/common';

interface Props {
  classes: { readonly [key: string]: string };
  onDone: (user: IUser) => void;
}

export function BasicInfo({ classes, onDone }: Props): JSX.Element {
  const { building, floors, chimeMediaRegion, blankEmailField } = useContext(
    BuildingContext,
  );

  const userParams = useMemo(() => getUserParams(floors), [floors]);

  const createUser = useRequest<Request.CreateUser>(
    HttpMethod.POST,
    '/api/user',
  );

  const [name, setName] = useState(userParams.name);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit: FormEventHandler = useCallback(
    evt => {
      evt.preventDefault();

      if (!chimeMediaRegion) return;

      createUser({
        ...userParams,
        name,
        email: email || blankEmailField,
        password,
        title: '',
        link: '',
        fields: [],
        chimeMediaRegion,
        href: window.location.href,
      })
        .then(({ user }) => {
          EventTracker.identify(user, building);

          onDone(user);
        })
        .catch(handleRequestError);
    },
    [
      blankEmailField,
      building,
      chimeMediaRegion,
      createUser,
      email,
      name,
      onDone,
      password,
      userParams,
    ],
  );

  return (
    <form className={classes.form} onSubmit={handleSubmit}>
      <TextInput
        label="Name"
        required
        autoComplete="name"
        className={classes.inputWrapper}
        InputProps={{
          className: classes.input,
        }}
        InputLabelProps={{
          className: classes.label,
        }}
        value={name}
        onChange={evt => setName(evt.target.value)}
        data-cy="name-input"
      />
      {building?.emailSettings !== EmailSettings.DISABLED && (
        <TextInput
          label="Email address"
          type="email"
          className={classes.inputWrapper}
          InputProps={{
            className: classes.input,
          }}
          InputLabelProps={{
            className: classes.label,
          }}
          value={email}
          onChange={evt => setEmail(evt.target.value)}
        />
      )}
      {building?.passwordProtected && (
        <TextInput
          label="Password"
          type="password"
          className={classes.inputWrapper}
          InputProps={{
            className: classes.input,
          }}
          InputLabelProps={{
            className: classes.label,
          }}
          value={password}
          onChange={evt => setPassword(evt.target.value)}
        />
      )}

      <p>
        Gatherly requires access to your webcam and microphone.
        <br />
        By clicking continue you agree to our{' '}
        <Clickable
          className={classes.link}
          href="https://www.gatherly.io/legal/terms-of-use"
          data-cy="terms-of-use"
        >
          Terms of Use
        </Clickable>{' '}
        and{' '}
        <Clickable
          className={classes.link}
          href="https://www.gatherly.io/legal/privacy-policy"
          data-cy="privacy-policy"
        >
          Privacy Policy
        </Clickable>
        .
      </p>

      <Button primary className={classes.button} type="submit" data-cy="submit">
        Continue
      </Button>
    </form>
  );
}
